import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, Star, Clock, CheckCircle2, ChevronDown } from "lucide-react";
import { CONTACT, waLink, DEFAULT_WA_MESSAGE, STATS, HERO_IMAGE, isOpenNow } from "@/data/content";

const CHIPS = ["Fırınlı Oto Boya", "Boyasız Göçük (PDR)", "Mobil Onarım", "Cam & Plastik Onarımı"];

const maskLine = {
  hidden: { y: "112%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.15 + i * 0.13, ease: [0.22, 1, 0.36, 1] },
  }),
};

const fade = (d) => ({
  initial: { opacity: 0, y: 18 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.7, delay: d, ease: "easeOut" },
});

export const Hero = () => {
  const open = isOpenNow();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 70]);
  const gridY = useTransform(scrollYProgress, [0, 1], [0, 30]);

  return (
    <section id="anasayfa" ref={ref} className="relative overflow-hidden bg-brand-softer pt-24 lg:pt-32">
      <motion.div style={{ y: gridY }} className="hero-grid-bg absolute inset-0" aria-hidden="true" />
      <div
        className="absolute -top-32 right-[-10%] h-96 w-96 rounded-full bg-brand/10 blur-3xl"
        aria-hidden="true"
      />
      <div
        className="absolute bottom-0 left-[-8%] h-72 w-72 rounded-full bg-skydeep/10 blur-3xl"
        aria-hidden="true"
      />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid items-center gap-12 py-12 lg:grid-cols-[1.05fr_0.95fr] lg:gap-16 lg:py-20">
          <div>
            <motion.span
              {...fade(0.05)}
              data-testid="hero-badge"
              className="inline-flex items-center gap-2 rounded-full border border-brand/20 bg-white px-4 py-1.5 text-xs font-bold uppercase tracking-[0.18em] text-brand"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-brand" />
              Adana Seyhan • Güvenilir Oto Onarım
            </motion.span>

            <h1 className="mt-6 font-display text-4xl font-extrabold leading-[1.1] tracking-tight text-slate-900 sm:text-5xl lg:text-6xl">
              <span className="block overflow-hidden pb-2">
                <motion.span
                  className="block"
                  variants={maskLine}
                  initial="hidden"
                  animate="show"
                  custom={0}
                >
                  Aracınız için kusursuz
                </motion.span>
              </span>
              <span className="block overflow-hidden pb-2">
                <motion.span
                  className="block"
                  variants={maskLine}
                  initial="hidden"
                  animate="show"
                  custom={1}
                >
                  <span className="relative whitespace-nowrap text-brand">
                    kaporta, boya
                    <svg
                      className="absolute -bottom-2 left-0 w-full text-brand/40"
                      viewBox="0 0 200 8"
                      fill="none"
                      aria-hidden="true"
                    >
                      <path
                        d="M2 6C40 2 120 2 198 5"
                        stroke="currentColor"
                        strokeWidth="3"
                        strokeLinecap="round"
                      />
                    </svg>
                  </span>
                </motion.span>
              </span>
              <span className="block overflow-hidden pb-2">
                <motion.span
                  className="block"
                  variants={maskLine}
                  initial="hidden"
                  animate="show"
                  custom={2}
                >
                  ve mekanik servisi
                </motion.span>
              </span>
            </h1>

            <motion.p
              {...fade(0.6)}
              className="mt-6 max-w-xl text-lg leading-relaxed text-slate-600"
            >
              Sistemli Otomotiv, Seyhan Adana'da 1.026 m² kapalı atölyesinde kaporta, fırınlı
              boya, mekanik ve elektrik servislerini tek çatı altında sunar.
            </motion.p>

            <motion.div {...fade(0.75)} className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
              <a
                href={waLink(DEFAULT_WA_MESSAGE)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-whatsapp-cta"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-whatsapp px-7 py-4 text-base font-bold text-white shadow-soft transition-all hover:-translate-y-0.5 hover:bg-whatsapp-dark hover:shadow-lift"
              >
                WhatsApp ile Anında Fiyat Al
              </a>
              <a
                href={CONTACT.phoneHref}
                data-testid="hero-phone-cta"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-300 bg-white px-7 py-4 text-base font-bold text-slate-800 transition-all hover:border-brand hover:text-brand"
              >
                <Phone className="h-4.5 w-4.5" />
                Hemen Ara: {CONTACT.phoneDisplay}
              </a>
            </motion.div>

            <motion.div {...fade(0.9)} className="mt-8 flex flex-wrap gap-2.5">
              {CHIPS.map((c) => (
                <span
                  key={c}
                  className="inline-flex items-center gap-1.5 rounded-full bg-white px-3.5 py-1.5 text-xs font-semibold text-slate-700 shadow-soft"
                >
                  <CheckCircle2 className="h-3.5 w-3.5 text-brand" />
                  {c}
                </span>
              ))}
            </motion.div>
          </div>

          <div className="relative">
            <motion.img
              src={HERO_IMAGE}
              alt="Sistemli Otomotiv'in Seyhan Adana'daki kapalı atölyesi ve servis salonu"
              style={{ y: imgY }}
              initial={{ opacity: 0, scale: 1.05 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.1, delay: 0.35, ease: "easeOut" }}
              className="h-72 w-full rounded-[2rem] object-cover shadow-lift sm:h-96 lg:h-[520px]"
              data-testid="hero-image"
            />
            <motion.div
              {...fade(0.95)}
              className="absolute -bottom-5 left-4 flex items-center gap-3 rounded-2xl border border-slate-100 bg-white px-5 py-4 shadow-lift sm:left-6"
              data-testid="hero-status-card"
            >
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-softer text-brand">
                <Clock className="h-5 w-5" />
              </span>
              <div>
                <p className="flex items-center gap-1.5 text-sm font-bold text-slate-900">
                  <span
                    className={`h-2 w-2 rounded-full ${open ? "bg-whatsapp" : "bg-amber-500"}`}
                  />
                  {open ? "Şu an açığız" : "Şu an kapalıyız"}
                </p>
                <p className="text-xs font-medium text-slate-500">Pzt–Cmt: 08:00 – 19:00 • Pazar kapalı</p>
              </div>
            </motion.div>
            <motion.div
              {...fade(1.05)}
              className="absolute -top-4 right-4 rounded-2xl border border-slate-100 bg-white px-5 py-4 shadow-lift sm:right-6"
              data-testid="hero-rating-card"
            >
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Star
                    key={i}
                    className={`h-4 w-4 ${i <= 4 ? "fill-amber-400 text-amber-400" : "fill-amber-400/50 text-amber-400"}`}
                  />
                ))}
              </div>
              <p className="mt-1 text-sm font-bold text-slate-900">
                4.3 / 5 <span className="font-medium text-slate-500">müşteri puanı</span>
              </p>
            </motion.div>
          </div>
        </div>

        <div className="relative pb-14">
          <div
            className="grid grid-cols-2 gap-px overflow-hidden rounded-3xl border border-slate-200 bg-slate-200 shadow-soft md:grid-cols-4"
            data-testid="hero-stats"
          >
            {STATS.map((s) => (
              <div key={s.label} className="bg-white px-6 py-6 text-center">
                <p className="font-display text-2xl font-extrabold text-slate-900 sm:text-3xl">
                  {s.value}
                </p>
                <p className="mt-1 text-xs font-semibold uppercase tracking-wider text-slate-500">
                  {s.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <a
        href="#hizmetler"
        data-testid="hero-scroll-down"
        aria-label="Hizmetlere geç"
        className="absolute bottom-4 left-1/2 hidden -translate-x-1/2 text-slate-400 transition-colors hover:text-brand lg:block"
      >
        <ChevronDown className="h-6 w-6 animate-bounce" />
      </a>
    </section>
  );
};

export default Hero;
